import { useState } from 'react';
import {
  Users,
  MapPin,
  Wrench,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Settings,
  TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line
} from 'recharts';

interface ResourceManagementProps {
  resourceData: any;
}

export function ResourceManagement({ resourceData }: ResourceManagementProps) {
  const [_selectedTimeframe, _setSelectedTimeframe] = useState('week');

  // Mock data for resource optimization suggestions
  const optimizationSuggestions = [
    {
      type: 'coach',
      priority: 'high',
      title: 'Redistribute Coach Workload',
      description: 'Lisa Brown is at 88% capacity. Consider moving 2-3 students to Mike Davis (70% capacity).',
      impact: 'Improved coach satisfaction and session quality',
      effort: 'Low'
    },
    {
      type: 'facility',
      priority: 'medium',
      title: 'Optimize Peak Hour Scheduling',
      description: 'Main Practice Net is underutilized during 2-4 PM. Offer incentives for off-peak sessions.',
      impact: 'Increased facility utilization from 70% to 85%',
      effort: 'Medium'
    },
    {
      type: 'equipment',
      priority: 'high',
      title: 'Schedule Equipment Maintenance',
      description: '3 analysis cameras need maintenance. Schedule during low-usage period.',
      impact: 'Prevent equipment downtime',
      effort: 'Low'
    }
  ];

  // Mock weekly workload data
  const workloadTrend = [
    { week: 'W1', coaches: 75, facilities: 68, equipment: 82 },
    { week: 'W2', coaches: 78, facilities: 71, equipment: 85 },
    { week: 'W3', coaches: 80, facilities: 65, equipment: 79 },
    { week: 'W4', coaches: 82, facilities: 69, equipment: 88 }
  ];

  const getUtilizationColor = (value: number) => {
    if (value >= 90) return 'text-red-600';
    if (value >= 80) return 'text-yellow-600';
    if (value >= 60) return 'text-green-600';
    return 'text-blue-600';
  };

  const getUtilizationStatus = (value: number) => {
    if (value >= 90) return 'Overloaded';
    if (value >= 80) return 'High';
    if (value >= 60) return 'Optimal';
    return 'Underutilized';
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <Badge variant="destructive">High Priority</Badge>;
      case 'medium':
        return <Badge variant="secondary">Medium Priority</Badge>;
      case 'low':
        return <Badge variant="outline">Low Priority</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Resource Overview Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Coach Utilization</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">78%</div>
            <Progress value={78} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-2">
              Average across all coaches
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Facility Usage</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">65%</div>
            <Progress value={65} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-2">
              Peak utilization during 4-8 PM
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Equipment Status</CardTitle>
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">82%</div>
            <Progress value={82} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-2">
              11 items need maintenance
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Optimization Suggestions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <TrendingUp className="mr-2 h-5 w-5" />
            Resource Optimization Recommendations
          </CardTitle>
          <CardDescription>
            AI-powered suggestions to improve resource allocation and utilization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {optimizationSuggestions.map((suggestion, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold">{suggestion.title}</h4>
                      {getPriorityBadge(suggestion.priority)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {suggestion.description}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Impact:</span> {suggestion.impact}
                      </div>
                      <div>
                        <span className="font-medium">Effort:</span> {suggestion.effort}
                      </div>
                    </div>
                  </div>
                  <div className="ml-4">
                    <Button size="sm">Apply</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Resource Details Tabs */}
      <Tabs defaultValue="coaches" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="coaches">Coach Workload</TabsTrigger>
          <TabsTrigger value="facilities">Facility Usage</TabsTrigger>
          <TabsTrigger value="equipment">Equipment Status</TabsTrigger>
        </TabsList>

        <TabsContent value="coaches" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Coach Workload Distribution</CardTitle>
                <CardDescription>
                  Current workload across all academy coaches
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Coach</TableHead>
                      <TableHead>Students</TableHead>
                      <TableHead>Hours/Week</TableHead>
                      <TableHead>Utilization</TableHead>
                      <TableHead>Rating</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resourceData?.coachWorkload?.map((coach: any) => (
                      <TableRow key={coach.id}>
                        <TableCell className="font-medium">{coach.name}</TableCell>
                        <TableCell>{coach.activeStudents}</TableCell>
                        <TableCell>{coach.weeklyHours}h</TableCell>
                        <TableCell>
                          <span className={getUtilizationColor(coach.utilization)}>
                            {coach.utilization}%
                          </span>
                          <div className="text-xs text-muted-foreground">
                            {getUtilizationStatus(coach.utilization)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            {coach.rating}★
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Workload Trends</CardTitle>
                <CardDescription>
                  Weekly utilization trends by resource type
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={workloadTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="coaches" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      name="Coaches"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="facilities" 
                      stroke="#10B981" 
                      strokeWidth={2}
                      name="Facilities"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="equipment" 
                      stroke="#F59E0B" 
                      strokeWidth={2}
                      name="Equipment"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="facilities" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Facility Utilization</CardTitle>
                <CardDescription>
                  Current usage across all training facilities
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Facility</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Booked Hours</TableHead>
                      <TableHead>Utilization</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resourceData?.facilityUsage?.map((facility: any) => (
                      <TableRow key={facility.id}>
                        <TableCell className="font-medium">{facility.name}</TableCell>
                        <TableCell className="capitalize">{facility.type}</TableCell>
                        <TableCell>{facility.hoursBooked}/{facility.maxHours}h</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Progress value={facility.utilization} className="w-16" />
                            <span className={getUtilizationColor(facility.utilization)}>
                              {facility.utilization}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={facility.utilization > 80 ? "destructive" : "secondary"}>
                            {getUtilizationStatus(facility.utilization)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Facility Schedule Optimization</CardTitle>
                <CardDescription>
                  Peak hours and scheduling recommendations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Peak Usage Times</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-red-600 font-medium">High Demand</div>
                        <div>4:00 PM - 8:00 PM</div>
                        <div>Saturday 9:00 AM - 2:00 PM</div>
                      </div>
                      <div>
                        <div className="text-green-600 font-medium">Available</div>
                        <div>10:00 AM - 2:00 PM</div>
                        <div>Sunday mornings</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Recommendations</h4>
                    <ul className="text-sm space-y-2">
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
                        Offer 10% discount for off-peak sessions (10 AM - 2 PM)
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
                        Block book facilities for group sessions during low-demand periods
                      </li>
                      <li className="flex items-start">
                        <AlertTriangle className="h-4 w-4 text-yellow-500 mr-2 mt-0.5" />
                        Consider extending Saturday hours to reduce congestion
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="equipment" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Equipment Inventory</CardTitle>
                <CardDescription>
                  Current equipment status and availability
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Equipment Type</TableHead>
                      <TableHead>Available</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Maintenance</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resourceData?.equipmentStatus?.map((equipment: any) => (
                      <TableRow key={equipment.id}>
                        <TableCell className="font-medium">{equipment.type}</TableCell>
                        <TableCell>{equipment.available}</TableCell>
                        <TableCell>{equipment.total}</TableCell>
                        <TableCell>
                          {equipment.maintenanceScheduled > 0 && (
                            <Badge variant="outline">
                              {equipment.maintenanceScheduled} pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            equipment.available / equipment.total > 0.8 ? "default" : 
                            equipment.available / equipment.total > 0.6 ? "secondary" : "destructive"
                          }>
                            {Math.round((equipment.available / equipment.total) * 100)}% Available
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Maintenance Schedule</CardTitle>
                <CardDescription>
                  Upcoming maintenance and replacement needs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-2 flex items-center">
                      <AlertTriangle className="h-4 w-4 text-yellow-500 mr-2" />
                      Urgent Maintenance
                    </h4>
                    <ul className="text-sm space-y-2">
                      <li>• 2 Bowling Machines - Service due</li>
                      <li>• 3 Analysis Cameras - Calibration needed</li>
                      <li>• 1 Batting Cage - Net replacement</li>
                    </ul>
                  </div>
                  
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-2 flex items-center">
                      <Calendar className="h-4 w-4 text-blue-500 mr-2" />
                      Scheduled This Week
                    </h4>
                    <ul className="text-sm space-y-2">
                      <li>• Monday: Bowling machine service (Units 3, 7)</li>
                      <li>• Wednesday: Camera calibration (Courts 1-3)</li>
                      <li>• Friday: Protective gear inspection</li>
                    </ul>
                  </div>

                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-2 flex items-center">
                      <Settings className="h-4 w-4 text-green-500 mr-2" />
                      Optimization Tips
                    </h4>
                    <ul className="text-sm space-y-2">
                      <li>• Schedule maintenance during low-usage periods (Tue/Thu mornings)</li>
                      <li>• Implement preventive maintenance program</li>
                      <li>• Consider equipment rotation to extend lifespan</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}